import {
  normalizeVideoProvider,
  VIDEO_PROVIDER_ENG_GUAVIDEO,
  VIDEO_PROVIDER_GUAVIDEO,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
} from "./projectRuntime";

export const URL_MODE_ASSET_STATUS = "URL_MODE";

const VIDEO_ASSET_FIELDS = Object.freeze({
  [VIDEO_PROVIDER_GUAVIDEO]: Object.freeze({
    idField: "guaVideoAssetId",
    statusField: "guaVideoAssetStatus",
  }),
  dreamina_proxy: Object.freeze({
    idField: "dreaminaProxyAssetId",
    statusField: "dreaminaProxyAssetStatus",
  }),
});

const assetLibraryProvider = (provider) => {
  const normalized = normalizeVideoProvider(provider);
  if (normalized === VIDEO_PROVIDER_ENG_GUAVIDEO || normalized === VIDEO_PROVIDER_OMINI_SEEDANCE
      || String(provider || "").trim().toLowerCase() === "dreamina_proxy") return "dreamina_proxy";
  return normalized;
};

const isDreaminaProxyAssetProvider = (provider) => assetLibraryProvider(provider) === "dreamina_proxy";

export const isSameVideoAssetLibrary = (left, right) => {
  const leftLibrary = assetLibraryProvider(left);
  return Boolean(leftLibrary && leftLibrary === assetLibraryProvider(right));
};

export const getVideoAssetFieldNames = (provider) => (
  VIDEO_ASSET_FIELDS[assetLibraryProvider(provider)] || null
);

export const clearVideoAssetBinding = (asset = {}) => ({
  ...asset,
  guaVideoAssetId: null,
  guaVideoAssetStatus: null,
  engGuaVideoAssetId: null,
  engGuaVideoAssetStatus: null,
  guaOminiAssetId: null,
  guaOminiAssetStatus: null,
  dreaminaProxyAssetId: null,
  dreaminaProxyAssetStatus: null,
});

export const normalizeVideoAssetStatus = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (value === "active" || value === "ready" || value === "succeeded" || value === "success") return "Active";
  if (["processing", "pending", "queued", "running", "created"].includes(value)) return "Processing";
  if (["failed", "error", "expired", "cancelled", "canceled"].includes(value)) return "Failed";
  if (value === URL_MODE_ASSET_STATUS.toLowerCase()) return URL_MODE_ASSET_STATUS;
  return String(status || "").trim();
};

const detectResultProvider = (result = {}) => {
  const providers = Object.entries(VIDEO_ASSET_FIELDS)
    .filter(([, fields]) => result?.[fields.idField] || result?.[fields.statusField])
    .map(([provider]) => provider);
  if (providers.length > 1) return null;
  return providers.length === 1 ? providers[0] : "";
};

const resolveResultProvider = (result, expectedProvider = "") => {
  const expected = normalizeVideoProvider(expectedProvider);
  const rawProvider = String(result?.provider || result?.assetProvider || "").trim();
  const explicit = rawProvider.toLowerCase() === "dreamina_proxy"
    ? "dreamina_proxy"
    : normalizeVideoProvider(rawProvider);
  if (rawProvider && !explicit) return "";

  const detected = detectResultProvider(result);
  if (detected === null) return "";
  if (explicit && detected && assetLibraryProvider(explicit) !== detected) return "";
  const resolved = expected || explicit || detected;
  if (expected && explicit && assetLibraryProvider(explicit) !== assetLibraryProvider(expected)) return "";
  if (expected && detected && detected !== assetLibraryProvider(expected)) return "";
  return resolved;
};

export const isUrlModeAssetResult = (result) => {
  const status = result?.status || result?.Status;
  return result?.skipped === true || status === URL_MODE_ASSET_STATUS;
};

export const normalizeAssetCreateResult = (result, expectedProvider = "") => {
  if (!result) return null;
  const expected = normalizeVideoProvider(expectedProvider);
  let provider = resolveResultProvider(result, expectedProvider);
  if (!provider && !expected && result.assetId) provider = VIDEO_PROVIDER_GUAVIDEO;
  if (!provider && !expected && isUrlModeAssetResult(result)) provider = VIDEO_PROVIDER_GUAVIDEO;
  const fields = getVideoAssetFieldNames(provider);
  if (!fields) return null;

  if (isUrlModeAssetResult(result)) {
    // Providers with an isolated material library must never silently degrade
    // to URL-only mode, otherwise references can cross provider boundaries.
    if (isDreaminaProxyAssetProvider(provider)) return null;
    return {
      accepted: true,
      provider,
      assetId: "",
      status: URL_MODE_ASSET_STATUS,
      urlMode: true,
    };
  }
  // The generic assetId may belong to a historical EngGuaVideo/GuaOmini library.
  // A Dreamina v3 response must explicitly carry the new library's field.
  const assetId = result?.[fields.idField]
    || (isDreaminaProxyAssetProvider(provider) ? "" : result.assetId);
  if (assetId) {
    return {
      accepted: true,
      provider,
      assetId,
      status: normalizeVideoAssetStatus(result?.[fields.statusField] || result.status || result.Status || "Processing"),
      urlMode: false,
    };
  }
  return null;
};

export const applyAssetCreateResult = (target, result, expectedProvider = "") => {
  const normalized = normalizeAssetCreateResult(result, expectedProvider);
  if (!normalized?.accepted) {
    return { target, accepted: false, urlMode: false };
  }
  const fields = getVideoAssetFieldNames(normalized.provider);
  if (!fields) return { target, accepted: false, urlMode: false };
  return {
    target: {
      ...target,
      [fields.idField]: normalized.assetId,
      [fields.statusField]: normalized.status,
    },
    accepted: true,
    urlMode: normalized.urlMode,
    provider: normalized.provider,
  };
};

export const isAssetReadyStatus = (status) => (
  normalizeVideoAssetStatus(status) === "Active" || normalizeVideoAssetStatus(status) === URL_MODE_ASSET_STATUS
);

export const getEffectiveAssetMeta = (asset = {}, preferredProvider = "") => {
  const provider = normalizeVideoProvider(preferredProvider);
  const fields = getVideoAssetFieldNames(provider);
  if (!fields) {
    return { provider: "", assetId: "", status: "", isOverseas: false, urlMode: false };
  }
  const status = normalizeVideoAssetStatus(asset?.[fields.statusField] || "");
  return {
    provider,
    assetId: asset?.[fields.idField] || "",
    status,
    isOverseas: isDreaminaProxyAssetProvider(provider),
    urlMode: status === URL_MODE_ASSET_STATUS,
  };
};

export const applyAssetStatusResult = (target, result, expectedProvider) => {
  const provider = resolveResultProvider(result, expectedProvider);
  const fields = getVideoAssetFieldNames(provider);
  if (!fields) return { target, accepted: false, provider: "", status: "" };
  const status = normalizeVideoAssetStatus(result?.[fields.statusField]
    || (isDreaminaProxyAssetProvider(provider) ? "" : result?.status || result?.Status));
  if (!status) return { target, accepted: false, provider, status: "" };
  return {
    target: { ...target, [fields.statusField]: status },
    accepted: true,
    provider,
    status,
  };
};

export const assetLibraryLabel = (provider) => (
  isDreaminaProxyAssetProvider(provider)
    ? "Dreamina v3 海外素材库"
      : normalizeVideoProvider(provider) === VIDEO_PROVIDER_GUAVIDEO
        ? "GuaVideo 国内素材库"
        : "当前项目素材库"
);

export const assetStatusLabel = (status, fallback = "素材入库") => {
  const normalizedStatus = normalizeVideoAssetStatus(status);
  if (normalizedStatus === URL_MODE_ASSET_STATUS) return "URL模式";
  if (normalizedStatus === "Active") return "已入库";
  if (normalizedStatus === "Failed") return "入库失败";
  if (normalizedStatus === "Processing") return "处理中...";
  return fallback;
};
